import { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { GripVertical, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export const WIDGET_HEADER_FLUSH = 'p-0';

/** Classe de la poignée que la grille du tableau de bord écoute pour déplacer un bloc. */
export const WIDGET_DRAG_HANDLE_CLASS = 'cime-widget-drag';

/** Vrai quand le bloc est posé dans la grille déplaçable du tableau de bord. */
export const DashboardWidgetHandleContext = createContext(false);

export function WidgetDragHandle() {
  const draggable = useContext(DashboardWidgetHandleContext);
  if (!draggable) return null;

  return (
    <button
      type="button"
      aria-label="Déplacer"
      title="Déplacer"
      className={cn(
        WIDGET_DRAG_HANDLE_CLASS,
        'shrink-0 p-1 rounded text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 cursor-grab active:cursor-grabbing',
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <GripVertical className="w-4 h-4" aria-hidden />
    </button>
  );
}

export function WidgetCardHeader({
  icon: Icon,
  title,
  href,
  extra,
}: {
  icon?: LucideIcon;
  title: string;
  href?: string;
  extra?: React.ReactNode;
}) {
  const inner = (
    <>
      {Icon ? <Icon className="w-4 h-4 text-cime-primary shrink-0" aria-hidden /> : null}
      <span className="text-sm font-semibold text-neutral-700 truncate flex-1 min-w-0">
        {title}
      </span>
      {extra}
      {href ? (
        <ChevronRight
          className="w-4 h-4 shrink-0 text-cime-primary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
          aria-hidden
        />
      ) : null}
    </>
  );

  return (
    <div className="flex items-stretch min-h-[3.25rem]">
      <div className="flex items-center pl-3 pr-1">
        <WidgetDragHandle />
      </div>
      {href ? (
        <Link
          to={href}
          className={cn(
            'group flex-1 min-w-0 flex items-center gap-2 px-2 pr-4',
            'hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none transition-colors',
          )}
        >
          {inner}
        </Link>
      ) : (
        <div className="flex-1 min-w-0 flex items-center gap-2 px-2 pr-4">{inner}</div>
      )}
    </div>
  );
}
